import { RecordCollection } from '../../app/record-collection.js';
import { Eye } from 'lucide-react';
import {
  ListChecks as CardIconListChecks,
  MapPin as CardIconMapPin,
  Package as CardIconPackage,
} from 'lucide-react';
import { CardHeader as ContentCardHeader } from '@hue/design-system';
import type { ReactNode } from 'react';
import {
  Button,
  ChoiceChips,
  Combobox,
  CurrencyInput,
  DatePicker,
  FormErrorSummary,
  HelpTip,
  IconButton,
  Input,
  StatusPill,
  ToggleChips,
  formatCurrency,
  useSnackbar,
} from '@hue/design-system';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Minus, PackageOpen, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  EmptyCard,
  ErrorCard,
  FilterBar,
  LoadingCard,
  Pagination,
  RequestAlert,
} from '../../app/data-view.js';
import type { SalesTermsView } from '../../app/list-views.js';
import type { ViewSetter } from '../../app/view-state.js';
import {
  getApiError,
  getApiErrorCode,
  salesApi,
  type SalesFulfillment,
  type SalesItemKind,
  type SalesPackage,
  type SalesPackageInput,
  type SalesTermStatus,
} from '../../lib/api.js';
import { PACKAGE_STATUS, allocateCents, termText, unitsText } from './sales-terms.js';
import { CreateButton, PageHeader } from '../../app/page-title.js';

const SEARCH_DELAY_MS = 250;
const MIN_UNITS = 2;

export interface SalesUnit {
  id: string;
  displayName: string;
}

/// A lista de pacotes: situação pelas datas, busca e paginação no servidor. O cartão inteiro abre
/// a página do pacote.
export function SalesPackagesList({
  view,
  onView,
  units,
  canManage,
  onOpen,
}: {
  view: SalesTermsView;
  onView: ViewSetter<SalesTermsView>;
  units: readonly SalesUnit[];
  canManage: boolean;
  onOpen: (packageId: string) => void;
}) {
  const search = view.search.trim();
  const page = useQuery({
    queryKey: [
      'company',
      'sales',
      'packages',
      view.term,
      search,
      view.limit,
      view.offset,
      view.sort,
      view.direction,
    ],
    queryFn: () =>
      salesApi.packages({
        ...(search ? { search } : {}),
        status: view.term,
        ...(view.sort ? { sort: view.sort, direction: view.direction ?? 'asc' } : {}),
        limit: view.limit,
        offset: view.offset,
      }),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const total = page.data?.total ?? 0;
  const status = PACKAGE_STATUS[view.term];
  return (
    <section aria-labelledby="sales-packages-title">
      <PageHeader
        trail="Catálogo / Pacotes"
        id="sales-packages-title"
        title="Pacotes"
        description="Serviços e produtos reunidos sob um preço único, vendidos nas unidades escolhidas."
        actions={
          canManage ? <CreateButton onClick={() => onOpen('new')}>Novo pacote</CreateButton> : null
        }
      />
      <FilterBar
        searchId="sales-packages-search"
        searchLabel="Buscar pacote"
        searchPlaceholder="Digite o nome"
        search={view.search}
        onSearchChange={(next) => onView({ search: next, offset: 0 }, { replace: true })}
        chips={
          view.term === 'ACTIVE'
            ? []
            : [
                {
                  key: 'term',
                  label: PACKAGE_STATUS[view.term].plural,
                  onRemove: () => onView({ term: 'ACTIVE', offset: 0 }),
                },
              ]
        }
        onClear={() => onView({ search: '', term: 'ACTIVE', offset: 0 })}
        count={`${total} ${total === 1 ? 'pacote' : 'pacotes'}`}
        resultsLabel={`Ver ${total} ${total === 1 ? 'pacote' : 'pacotes'}`}
        pageSize={view.limit}
        onPageSizeChange={(limit) => onView({ limit, offset: 0 })}
      >
        <ChoiceChips
          label="Situação dos pacotes"
          value={view.term}
          onChange={(term) => onView({ term: term as SalesTermStatus, offset: 0 })}
          options={(Object.keys(PACKAGE_STATUS) as SalesTermStatus[]).map((key) => ({
            value: key,
            label: `${PACKAGE_STATUS[key].plural} · ${String(page.data?.counts[key] ?? 0)}`,
          }))}
        />
      </FilterBar>
      {page.isLoading ? (
        <LoadingCard label="Carregando pacotes" />
      ) : page.error ? (
        <ErrorCard message={getApiError(page.error)} />
      ) : total === 0 ? (
        <EmptyCard
          icon={PackageOpen}
          title={`Nenhum pacote ${status.label.toLowerCase()}`}
          description="Pacote reúne serviços e produtos sob um preço único e soma pelo menos 2 unidades."
        />
      ) : (
        <RecordCollection
          label="Pacotes"
          onOpen={(found) => onOpen(found.id)}
          records={page.data?.packages ?? []}
          recordKey={(found) => found.id}
          sorting={{
            sort: view.sort ?? 'name',
            direction: view.direction ?? 'asc',
            columns: ['name', 'date', 'price'],
            onToggle: (key) => {
              if (key === 'name' || key === 'date' || key === 'price')
                onView({
                  sort: key,
                  direction:
                    key === (view.sort ?? 'name') && (view.direction ?? 'asc') === 'asc'
                      ? 'desc'
                      : 'asc',
                  offset: 0,
                });
            },
          }}
          columns={[
            {
              key: 'name',
              width: '18%',
              label: 'Pacote',
              render: (found) => (
                <button
                  type="button"
                  className="text-left font-semibold hover:underline"
                  onClick={() => onOpen(found.id)}
                >
                  {found.displayName}
                </button>
              ),
            },
            {
              key: 'items',
              width: '27%',
              label: 'Itens',
              render: (found) => (
                <span className="text-xs text-content-muted">
                  {found.items
                    .map((item) => `${String(item.quantity)} ${item.referenceDisplayName}`)
                    .join(' · ')}
                </span>
              ),
            },
            {
              key: 'balance',
              label: 'Entrega',
              render: (found) => {
                const balance = found.items
                  .filter((item) => item.fulfillment === 'BALANCE')
                  .reduce((sum, item) => sum + item.quantity, 0);
                return (
                  <span className="text-xs text-content-muted">
                    {balance ? `${String(balance)} por saldo` : 'tudo entregue na hora'}
                  </span>
                );
              },
            },
            {
              key: 'units',
              label: 'Unidades',
              render: (found) => (
                <span className="text-xs text-content-muted">
                  {unitsText(found.businessUnitIds, units)}
                </span>
              ),
            },
            {
              key: 'date',
              width: '16%',
              label: 'Vigência',
              render: (found) => (
                <span className="text-xs text-content-muted">
                  {termText(found.validFrom, found.validUntil)}
                </span>
              ),
            },
            {
              key: 'price',
              label: 'Preço',
              render: (found) => (
                <strong className="whitespace-nowrap tabular-nums">
                  {formatCurrency(found.priceCents, found.currency)}
                </strong>
              ),
            },
            {
              key: 'status',
              label: 'Situação',
              render: (found) => (
                <StatusPill tone={PACKAGE_STATUS[found.status].tone}>
                  {PACKAGE_STATUS[found.status].label}
                </StatusPill>
              ),
            },
          ]}
          actions={(found) => (
            <IconButton
              label={`Abrir pacote ${found.displayName}`}
              icon={<Eye />}
              onClick={() => onOpen(found.id)}
            />
          )}
          card={(found) => <PackageCard found={found} units={units} onOpen={onOpen} />}
        />
      )}
      <Pagination
        offset={view.offset}
        limit={view.limit}
        total={total}
        onOffsetChange={(offset) => onView({ offset })}
        itemLabel={(count) => (count === 1 ? 'pacote' : 'pacotes')}
      />
    </section>
  );
}

function PackageCard({
  found,
  units,
  onOpen,
}: {
  found: SalesPackage;
  units: readonly SalesUnit[];
  onOpen: (packageId: string) => void;
}) {
  const status = PACKAGE_STATUS[found.status];
  const balance = found.items
    .filter((item) => item.fulfillment === 'BALANCE')
    .reduce((sum, item) => sum + item.quantity, 0);
  return (
    <article
      className="cursor-pointer rounded-2xl border border-surface-border bg-surface-card p-4 transition hover:border-brand-500/40"
      onClick={() => onOpen(found.id)}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <button
            type="button"
            className="cursor-pointer text-left font-semibold hover:underline"
            onClick={(event) => {
              event.stopPropagation();
              onOpen(found.id);
            }}
          >
            {found.displayName}
          </button>
          <p className="mt-1 text-xs text-content-muted">
            {found.items
              .map((item) => `${String(item.quantity)} ${item.referenceDisplayName}`)
              .join(' · ')}
          </p>
          <p className="mt-1 text-xs text-content-muted">
            {unitsText(found.businessUnitIds, units)} ·{' '}
            {balance ? `${String(balance)} por saldo` : 'tudo entregue na hora'} ·{' '}
            {termText(found.validFrom, found.validUntil)}
          </p>
        </div>
        <div className="grid justify-items-end gap-2">
          <strong className="whitespace-nowrap tabular-nums">
            {formatCurrency(found.priceCents, found.currency)}
          </strong>
          <StatusPill tone={status.tone}>{status.label}</StatusPill>
        </div>
      </div>
    </article>
  );
}

interface DraftItem {
  kind: SalesItemKind;
  referenceId: string;
  label: string;
  basePriceCents: number;
  quantity: number;
  fulfillment: SalesFulfillment;
}

function draftItems(found: SalesPackage | undefined): DraftItem[] {
  return (
    found?.items.map((item) => ({
      kind: item.kind,
      referenceId: item.referenceId,
      label: item.referenceDisplayName,
      /// O peso retratado é o preço-base vezes a quantidade, no dia em que o pacote foi salvo.
      basePriceCents: Math.round(item.allocationWeightCents / item.quantity),
      quantity: item.quantity,
      fulfillment: item.fulfillment,
    })) ?? []
  );
}

/// A página do pacote: dados, itens com o valor no saldo, unidades onde é vendido. Mudar vale só
/// para as próximas vendas (ADR 0054).
export function SalesPackagePage({
  packageId,
  rulesEditor,
  units,
  today,
  canManage,
  balancesContracted,
  onBack,
}: {
  /// `new` cadastra um pacote novo.
  packageId: string;
  rulesEditor?: ReactNode;
  units: readonly SalesUnit[];
  today: string;
  canManage: boolean;
  /// Entrega por saldo só existe com Saldos pré-pagos contratado.
  balancesContracted: boolean;
  onBack: () => void;
}) {
  const creating = packageId === 'new';
  const loaded = useQuery({
    queryKey: ['company', 'sales', 'package', packageId],
    queryFn: () => salesApi.package(packageId),
    enabled: !creating,
    retry: false,
  });
  if (!creating && loaded.isLoading) return <LoadingCard label="Carregando o pacote" />;
  if (!creating && loaded.error) return <ErrorCard message={getApiError(loaded.error)} />;
  return (
    <PackageForm
      key={loaded.data?.updatedAt ?? 'new'}
      rulesEditor={rulesEditor}
      found={loaded.data}
      units={units}
      today={today}
      canManage={canManage}
      balancesContracted={balancesContracted}
      onBack={onBack}
    />
  );
}

function PackageForm({
  found,
  rulesEditor,
  units,
  today,
  canManage,
  balancesContracted,
  onBack,
}: {
  found: SalesPackage | undefined;
  rulesEditor?: ReactNode;
  units: readonly SalesUnit[];
  today: string;
  canManage: boolean;
  balancesContracted: boolean;
  onBack: () => void;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [name, setName] = useState(found?.displayName ?? '');
  const [price, setPrice] = useState(found?.priceCents ?? 0);
  const [validFrom, setValidFrom] = useState<string | null>(found?.validFrom ?? today);
  const [validUntil, setValidUntil] = useState<string | null>(found?.validUntil ?? null);
  const [unitIds, setUnitIds] = useState<string[]>(
    () => found?.businessUnitIds ?? units.map((unit) => unit.id),
  );
  const [items, setItems] = useState<DraftItem[]>(() => draftItems(found));
  const [submitted, setSubmitted] = useState(false);
  const [nameTaken, setNameTaken] = useState<string>();
  const [failure, setFailure] = useState<string>();
  const [busy, setBusy] = useState(false);

  const quantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const errors = {
    name: name.trim().length < 2 ? 'Informe o nome do pacote.' : nameTaken,
    price: price <= 0 ? 'Informe um preço maior que zero.' : undefined,
    validFrom: validFrom ? undefined : 'Informe quando o pacote começa a ser vendido.',
    validUntil:
      validFrom && validUntil && validUntil < validFrom
        ? 'A data de fim vem antes do início.'
        : undefined,
    items: quantity < MIN_UNITS ? 'O pacote precisa somar pelo menos 2 unidades.' : undefined,
    units: unitIds.length === 0 ? 'Escolha pelo menos uma unidade.' : undefined,
  };
  const show = (message: string | undefined) => (submitted ? message : undefined);
  const shares = allocateCents(
    price,
    items.map((item) => item.basePriceCents * item.quantity),
  );
  const table = items.reduce((sum, item) => sum + item.basePriceCents * item.quantity, 0);

  async function save() {
    setSubmitted(true);
    setFailure(undefined);
    if (Object.values(errors).some(Boolean)) return;
    const input: SalesPackageInput = {
      displayName: name,
      description: found?.description ?? null,
      priceCents: price,
      currency: 'BRL',
      validFrom: validFrom!,
      validUntil,
      businessUnitIds: unitIds,
      items: items.map((item) => ({
        kind: item.kind,
        referenceId: item.referenceId,
        quantity: item.quantity,
        fulfillment: item.fulfillment,
      })),
    };
    setBusy(true);
    try {
      const saved = found
        ? await salesApi.updatePackage(found.id, input)
        : await salesApi.createPackage(input);
      await queryClient.invalidateQueries({ queryKey: ['company', 'sales'] });
      snackbar.success(`${saved.displayName} salvo. Vale para as próximas vendas.`);
      onBack();
    } catch (reason) {
      if (getApiErrorCode(reason) === 'SALES_PACKAGE_NAME_TAKEN') setNameTaken(getApiError(reason));
      else setFailure(getApiError(reason));
    } finally {
      setBusy(false);
    }
  }

  async function toggleArchived() {
    if (!found) return;
    setBusy(true);
    try {
      await salesApi.setPackageArchived(found.id, !found.archived);
      await queryClient.invalidateQueries({ queryKey: ['company', 'sales'] });
      snackbar.success(
        found.archived
          ? `${found.displayName} reativado.`
          : `${found.displayName} arquivado. O que já foi vendido continua valendo.`,
      );
      onBack();
    } catch (reason) {
      setFailure(getApiError(reason));
    } finally {
      setBusy(false);
    }
  }

  function changeItem(index: number, patch: Partial<DraftItem>) {
    setItems((current) =>
      current.map((item, position) => (position === index ? { ...item, ...patch } : item)),
    );
  }

  const status = found ? PACKAGE_STATUS[found.status] : undefined;
  return (
    <section aria-labelledby="sales-package-title">
      <PageHeader
        onBack={onBack}
        trail="Catálogo / Pacotes"
        id="sales-package-title"
        title={found?.displayName ?? 'Novo pacote'}
        aside={status ? <StatusPill tone={status.tone}>{status.label}</StatusPill> : null}
      />
      <form
        noValidate
        className="grid gap-5"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <fieldset
          disabled={!canManage}
          className="grid gap-4 rounded-2xl border border-surface-border bg-surface-card p-5"
        >
          <legend className="sr-only">Dados do pacote</legend>
          <ContentCardHeader
            title="Dados do pacote"
            icon={<CardIconPackage className="size-4" />}
          />
          <Input
            label="Nome"
            placeholder="Ex.: Pacote Verão"
            value={name}
            error={show(errors.name)}
            onChange={(event) => {
              setName(event.target.value);
              setNameTaken(undefined);
            }}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <CurrencyInput
              label="Preço do pacote"
              currency="BRL"
              value={price}
              error={show(errors.price)}
              onValueChange={(value) => setPrice(value ?? 0)}
            />
            <div className="hidden sm:block" />
            <DatePicker
              label="Começa a vender em"
              value={validFrom}
              onValueChange={setValidFrom}
              today={today}
              error={show(errors.validFrom)}
            />
            <DatePicker
              label="Para de vender em (opcional)"
              value={validUntil}
              onValueChange={setValidUntil}
              today={today}
              error={show(errors.validUntil)}
              hint={validUntil ? undefined : '∞ Sem data de fim'}
            />
          </div>
          <p className="text-xs text-content-muted">
            As datas controlam só a venda do pacote. Quem já comprou continua com o que comprou.
          </p>
        </fieldset>

        <fieldset
          disabled={!canManage}
          className="grid gap-3 rounded-2xl border border-surface-border bg-surface-card p-5"
        >
          <legend className="sr-only">Itens</legend>
          <div className="flex items-center gap-2">
            <ContentCardHeader title="Itens" icon={<CardIconListChecks className="size-4" />} />
            <HelpTip
              label="O que é o valor no saldo"
              content="É quanto vale cada sessão ou unidade dentro do pacote: o preço do pacote repartido pelo preço de tabela de cada item. Promoções de itens avulsos não entram nessa conta."
            />
          </div>
          <div
            role="group"
            aria-label="Itens"
            aria-invalid={Boolean(show(errors.items))}
            tabIndex={-1}
            className="grid gap-2"
          >
            {items.length === 0 ? (
              <p className="text-sm text-content-muted">
                Nenhum item ainda. Busque abaixo um serviço ou produto.
              </p>
            ) : (
              items.map((item, index) => (
                <PackageItemRow
                  key={`${item.kind}:${item.referenceId}`}
                  item={item}
                  shareCents={shares[index] ?? 0}
                  priceKnown={price > 0}
                  balancesContracted={balancesContracted}
                  onChange={(patch) => changeItem(index, patch)}
                  onRemove={() =>
                    setItems((current) => current.filter((_, position) => position !== index))
                  }
                />
              ))
            )}
          </div>
          {canManage ? (
            <PackageItemSearch
              taken={items}
              onPick={(picked) =>
                setItems((current) => [
                  ...current,
                  {
                    ...picked,
                    quantity: 1,
                    fulfillment: 'IMMEDIATE',
                  },
                ])
              }
            />
          ) : null}
          {show(errors.items) ? (
            <p role="alert" className="text-xs text-danger">
              {errors.items}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-surface-border pt-3 text-sm">
            <span>
              Na tabela <strong>{formatCurrency(table, 'BRL')}</strong>
            </span>
            <span>
              Preço do pacote <strong>{formatCurrency(price, 'BRL')}</strong>
            </span>
            {table > 0 && price > 0 ? (
              <span>
                <strong>
                  {Math.abs(((table - price) / table) * 100).toLocaleString('pt-BR', {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
                  %
                </strong>{' '}
                {price <= table ? 'abaixo da tabela' : 'acima da tabela'}
              </span>
            ) : null}
          </div>
        </fieldset>

        <fieldset
          disabled={!canManage}
          className="grid gap-3 rounded-2xl border border-surface-border bg-surface-card p-5"
        >
          <legend className="sr-only">Onde é vendido</legend>
          <ContentCardHeader title="Onde é vendido" icon={<CardIconMapPin className="size-4" />} />
          <div
            role="group"
            aria-label="Unidades"
            aria-invalid={Boolean(show(errors.units))}
            tabIndex={-1}
          >
            <ToggleChips
              label="Unidades onde o pacote é vendido"
              itemNoun="unidades"
              options={units.map((unit) => ({ value: unit.id, label: unit.displayName }))}
              selected={unitIds}
              onChange={setUnitIds}
            />
          </div>
          {show(errors.units) ? (
            <p role="alert" className="text-xs text-danger">
              {errors.units}
            </p>
          ) : null}
        </fieldset>

        {failure ? <RequestAlert message={failure} /> : null}
        {canManage ? (
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
            {found ? (
              <Button
                type="button"
                variant="ghost"
                disabled={busy}
                onClick={() => void toggleArchived()}
              >
                {found.archived ? 'Reativar pacote' : 'Arquivar pacote'}
              </Button>
            ) : null}
            <FormErrorSummary className="order-last sm:order-none sm:mr-auto sm:flex-1" />
            <Button type="button" variant="outline" onClick={onBack}>
              Cancelar
            </Button>
            <Button type="submit" disabled={busy}>
              Salvar pacote
            </Button>
          </div>
        ) : null}
      </form>
      {rulesEditor}
    </section>
  );
}

function PackageItemRow({
  item,
  shareCents,
  priceKnown,
  balancesContracted,
  onChange,
  onRemove,
}: {
  item: DraftItem;
  shareCents: number;
  priceKnown: boolean;
  balancesContracted: boolean;
  onChange: (patch: Partial<DraftItem>) => void;
  onRemove: () => void;
}) {
  const unit = item.kind === 'SERVICE' ? 'sessão' : 'unidade';
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-surface-elevated p-3">
      <div className="min-w-0 flex-1">
        <strong className="block">{item.label}</strong>
        <span className="text-xs text-content-muted">
          {item.kind === 'SERVICE' ? 'Serviço' : 'Produto'} · tabela{' '}
          {formatCurrency(item.basePriceCents, 'BRL')} ·{' '}
          {priceKnown
            ? `valor no saldo ${formatCurrency(Math.floor(shareCents / item.quantity), 'BRL')} por ${unit}`
            : 'informe o preço'}
        </span>
      </div>
      <div className="flex items-end gap-1">
        <IconButton
          label={`Diminuir quantidade de ${item.label}`}
          icon={<Minus className="size-4" />}
          disabled={item.quantity <= 1}
          onClick={() => onChange({ quantity: item.quantity - 1 })}
        />
        <div className="w-16">
          <Input
            label="Quantidade"
            aria-label={`Quantidade de ${item.label}`}
            className="w-16 text-center"
            inputMode="numeric"
            value={item.quantity || ''}
            onChange={(event) => {
              const text = event.target.value;
              const quantity = Number(text);
              if (/^\d*$/u.test(text) && quantity <= 10_000)
                onChange({ quantity: Math.max(1, quantity) });
            }}
          />
        </div>
        <IconButton
          label={`Aumentar quantidade de ${item.label}`}
          icon={<Plus className="size-4" />}
          onClick={() => onChange({ quantity: item.quantity + 1 })}
        />
      </div>
      {balancesContracted ? (
        <ChoiceChips
          label={`Entrega de ${item.label}`}
          value={item.fulfillment}
          onChange={(fulfillment) => onChange({ fulfillment: fulfillment as SalesFulfillment })}
          options={[
            { value: 'IMMEDIATE', label: 'Na hora' },
            { value: 'BALANCE', label: 'Por saldo' },
          ]}
        />
      ) : null}
      <IconButton
        label={`Tirar ${item.label}`}
        tone="danger"
        icon={<Trash2 className="size-4" />}
        onClick={onRemove}
      />
    </div>
  );
}

/// Busca no catálogo da venda — serviços e produtos vendáveis — pelo nome.
function PackageItemSearch({
  taken,
  onPick,
}: {
  taken: readonly DraftItem[];
  onPick: (item: Omit<DraftItem, 'quantity' | 'fulfillment'>) => void;
}) {
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setQuery(text.trim()), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [text]);
  const result = useQuery({
    queryKey: ['company', 'sales', 'catalog', query],
    queryFn: () => salesApi.catalog(query || undefined),
    placeholderData: keepPreviousData,
    retry: false,
  });
  const options = [...(result.data?.services ?? []), ...(result.data?.products ?? [])].filter(
    (item) => !taken.some((entry) => entry.kind === item.kind && entry.referenceId === item.id),
  );
  return (
    <Combobox
      label="Adicionar serviço ou produto"
      placeholder="Buscar pelo nome"
      selectedLabel=""
      clearable={false}
      options={options.map((item) => ({
        value: `${item.kind}:${item.id}`,
        label: item.displayName,
        description: `${item.kind === 'SERVICE' ? 'Serviço' : 'Produto'} · ${formatCurrency(item.basePriceCents, item.currency)}`,
      }))}
      onSelect={(choice) => {
        const { value } = choice as { value: string };
        const item = options.find((entry) => `${entry.kind}:${entry.id}` === value);
        if (item)
          onPick({
            kind: item.kind,
            referenceId: item.id,
            label: item.displayName,
            basePriceCents: item.basePriceCents,
          });
      }}
      onQueryChange={setText}
      loading={result.isFetching}
      emptyLabel="Nenhum serviço ou produto encontrado"
    />
  );
}
